import { FieldHasValue, ValidateField, ValidateNumberField, ExponentialToDecimal } from 'helpers/Validation';

const AMERICA_REGIONS = ['GULF OF MEXICO'];
const GLOBAL_REGIONS = ['OMAN', 'ANGOLA', 'TANGGUH LNG', 'NORTH SEA', 'M&S GTA', 'CASTELLON REFINERY', 'AGT', 'WHITING REFINERY', 'TRINIDAD & TOBAGO'];
const UNIT_STANDARD_L_HOUR = 'Standard L/hr';

const NPS_TO_DN_LOOKUP = {
  '1/4': 8,
  '3/8': 10,
  '1/2': 15,
  '3/4': 20,
  1: 25,
  '11/4': 32,
  '11/2': 40,
  2: 50,
  '21/2': 65,
  3: 80,
  4: 100,
  5: 125,
  6: 150,
  8: 200,
  10: 250,
  12: 300,
  14: 350,
  16: 400,
  18: 450,
  20: 500,
  24: 600,
  26: 650,
  28: 700,
  30: 750,
  32: 800,
  36: 900,
  40: 1000
};

const RATE_E_MULTIPLIER_LOOKUP = {
  liquid: 0.3,
  gas: 300
};

const PRESSURE_CLASS_CWP_LOOKUP = {
  150: 313.5,
  300: 814,
  400: 1083.5,
  600: 1628,
  900: 2442,
  1500: 4075.5,
  2500: 6787
};

const getCWP = (pressureClass) => {
    if (pressureClass <= 2500) {
        return PRESSURE_CLASS_CWP_LOOKUP[pressureClass];
    }
    return pressureClass * 1.1;
};

const calculateLeakageByMedia = (dnMultiplied, testMedia, CWP) => {
    if (testMedia === 'gas') {
        return dnMultiplied * 0.000008343 * (CWP + 14.7);
    }
    if (testMedia === 'liquid') {
        return dnMultiplied * 0.00000025031 * (0.0001 * CWP + 62.808);
    }
    return null;
};

const convertLeakageRate = (leakageValue, leakageRateEU) => {
    if (leakageRateEU === 'scfh') return leakageValue;
    if (leakageRateEU === 'standard l/hr') return leakageValue * 28.314;
    if (leakageRateEU === 'us-gpm') return leakageValue;
    if (leakageRateEU === 'l/min') return leakageValue * 3.785;
    return null;
};

const calculateRawGreenZoneValue = (formData) => {
    const NPS = formData?.F340ValveSize;
    const TestMedia = formData?.F1093TestMedia;
    const LeakageRateEU = formData?.F603LeakageRateEngUnit;
    const PressureClass = formData?.F1075ValvePressureClass;

    if (!FieldHasValue(NPS) || !FieldHasValue(TestMedia) || !FieldHasValue(PressureClass)) return null;

    const npsValue = ValidateField(NPS);
    const testMedia = String(ValidateField(TestMedia)).toLowerCase();
    const leakageRateEU = String(ValidateField(LeakageRateEU)).toLowerCase();
    const pressureClass = ValidateNumberField(PressureClass);

    const DN = NPS_TO_DN_LOOKUP[npsValue];
    if (!DN) return null;

    const rateEMultiplier = RATE_E_MULTIPLIER_LOOKUP[testMedia];
    if (rateEMultiplier === undefined) return null;

    const dnMultiplied = DN * rateEMultiplier * 20;

    const CWP = getCWP(pressureClass);
    if (!CWP) return null;

    const leakageValue = calculateLeakageByMedia(dnMultiplied, testMedia, CWP);
    if (leakageValue === null) return null;

    const result = convertLeakageRate(leakageValue, leakageRateEU);
    if (result === null) return null;

    return result;
};

export const AsFoundGreenZoneUpperLimitFormula = (formData) => {
    const result = calculateRawGreenZoneValue(formData);
    if (result === null) return '';
    return ExponentialToDecimal(Math.round(result * 100) / 100);
};

export const AsFoundYellowZoneUpperLimitFormula = (formData) => {
    const rawGreenZoneValue = calculateRawGreenZoneValue(formData);
    if (rawGreenZoneValue === null) return '';
    return ExponentialToDecimal(Math.round(rawGreenZoneValue * 5 * 100) / 100);
};
const convertToFahrenheit = (temperature) => (temperature * 1.8) + 32;
const convertToPsig = (pressure, pressureEU) => {
    const unit = String(pressureEU).toLowerCase();
    if (unit === 'barg') return pressure * 14.5038;
    if (unit === 'kpag') return pressure * 0.145038;
    return pressure;
};
const convertFieldReadingToSCFH = (fieldReading, leakageRateEU) => {
    const unit = String(leakageRateEU).toLowerCase();
    if (unit === 'standard l/hr') return fieldReading / 28.314;
    return fieldReading;
};
const convertSCFHToCFH = (fieldReadingSCFH, testTempF, upstreamPressurePsig) => fieldReadingSCFH * 14.7 * (testTempF + 460) / (492 * (upstreamPressurePsig + 14.7));
const calculateSpecificHeatRatio = (upstreamPressurePsig) => 0.0001 * upstreamPressurePsig + 1.4072;
const calculateChokedPressure = (upstreamPressurePsig, k) => (upstreamPressurePsig + 14.7) * Math.pow((2 / (k + 1)), (k / (k - 1))) - 14.7;
const calculateFieldDensity = (upstreamPressurePsig) => 0.0049 * upstreamPressurePsig + 0.0686;
const calculateOrificeDiameter = (fieldReadingCFH, chokedPressure, upstreamPressurePsig, k, chokedDiffPressure, fieldDensity) => {
    const numerator = fieldReadingCFH;
    const pressureRatio = (chokedPressure + 14.7) / (upstreamPressurePsig + 14.7);
    const term1 = 0.6 * (1 - 0.351 * (1 - Math.pow(pressureRatio, 1 / k)));
    const term2 = Math.PI / (4 * 144);
    const term3 = Math.sqrt((2 * 32.174 * chokedDiffPressure * 144) / fieldDensity);
    const term4 = 3600;
    return Math.sqrt(numerator / (term1 * term2 * term3 * term4));
};
const calculateArea = (orificeDiameter) => 3.14 * Math.pow(orificeDiameter, 2) / (4 * 144);
const calculateWaterFlow = (area, diffPressure, upstreamPressurePsig) => 0.65 * area * Math.pow((2 * 32.174 * diffPressure * 144) / (0.0001 * upstreamPressurePsig + 62.808), 0.5) * 3600;
const calculateWaterEquivalentTestFlow = (waterFlow, upstreamPressurePsig) => ((waterFlow * (0.0001 * upstreamPressurePsig + 62.808)) / 63.3266142316) * 1.1 * 7.4814 / 60;
const convertByLeakageRateEU = (value, leakageRateEU) => {
    const unit = String(leakageRateEU).toLowerCase();
    if (unit === 'l/min') return value * 3.785;
    return value;
};
export const WaterEquivalentTestFlowFormula = (formData) => {
    const TestMedia = formData?.F1093TestMedia;
    const TemperatureEU = formData?.F1094TemperatureEngineeringUnit;
    const PressureEU = formData?.F1086PressureEngineeringUnit;
    const LeakageRateEU = formData?.F603LeakageRateEngUnit;
    const TestTemperature = formData?.F1095TestTemperature;
    const UpstreamPressure = formData?.F1087UpstreamPressure;
    const DownstreamPressure = formData?.F1088DownstreamPressure;
    const FieldReading = formData?.F1091FieldReading;
    const NitrogenPSVReliefCapacity = formData?.F1096NitrogenPSVReliefCapacity;

    const testMedia = String(ValidateField(TestMedia)).toLowerCase();
    if (testMedia !== 'gas' || FieldHasValue(NitrogenPSVReliefCapacity)) {
        return '';
    }

    if (!FieldHasValue(TestTemperature) || !FieldHasValue(UpstreamPressure)
        || !FieldHasValue(DownstreamPressure) || !FieldHasValue(FieldReading)) {
        return '';
    }

    const testTemp = ValidateNumberField(TestTemperature);
    const upstreamPress = ValidateNumberField(UpstreamPressure);
    const downstreamPress = ValidateNumberField(DownstreamPressure);
    const fieldReading = ValidateNumberField(FieldReading);
    const tempEU = ValidateField(TemperatureEU);
    const pressEU = ValidateField(PressureEU);
    const leakageRateEU = ValidateField(LeakageRateEU);

    if (upstreamPress > downstreamPress) {
        return 'Ensure a reverse flow is created (upstream pressure < downstream pressure)';
    }

    const testTempF = String(tempEU).toLowerCase() === '°c' || String(tempEU).toLowerCase() === 'c'
        ? convertToFahrenheit(testTemp)
        : testTemp;

    const upstreamPressurePsig = convertToPsig(upstreamPress, pressEU);
    const downstreamPressurePsig = convertToPsig(downstreamPress, pressEU);
    const fieldReadingSCFH = convertFieldReadingToSCFH(fieldReading, leakageRateEU);
    const fieldReadingCFH = convertSCFHToCFH(fieldReadingSCFH, testTempF, upstreamPressurePsig);
    const heatRatio = calculateSpecificHeatRatio(upstreamPressurePsig);
    const chokedPressure = calculateChokedPressure(upstreamPressurePsig, heatRatio);
    const diffPressure = downstreamPressurePsig - upstreamPressurePsig;
    const chokedDiffPressure = upstreamPressurePsig - chokedPressure;
    const fieldDensity = calculateFieldDensity(upstreamPressurePsig);
    const orificeDiameter = calculateOrificeDiameter(
        fieldReadingCFH,
        chokedPressure,
        upstreamPressurePsig,
        heatRatio,
        chokedDiffPressure,
        fieldDensity
    );
    const area = calculateArea(orificeDiameter);
    const waterFlow = calculateWaterFlow(area, diffPressure, upstreamPressurePsig);
    const waterEquivTestFlow = calculateWaterEquivalentTestFlow(waterFlow, upstreamPressurePsig);
    const result = convertByLeakageRateEU(waterEquivTestFlow, leakageRateEU);

    return ExponentialToDecimal(Math.round(result * 100) / 100);
};
export const getPressureEngUnit = (formData) => formData.F1086PressureEngineeringUnit ? `(${formData.F1086PressureEngineeringUnit})` : '';
export const getRegionCategory = (regionAlias) => {
    if (!regionAlias) return 'Global';
    if (AMERICA_REGIONS.includes(regionAlias.toUpperCase())) return 'America';
    if (GLOBAL_REGIONS.includes(regionAlias.toUpperCase())) return 'Global';
    return 'Global';
};
export const generateUnitLabel = (baseLabel, formData) => {
    const unit = getPressureEngUnit(formData);
    return unit ? `${baseLabel} ${unit}` : `${baseLabel} ()`;
};
export const getGasUnit = (regionAlias) => {
    const regionCategory = getRegionCategory(regionAlias);
    return regionCategory === 'America' ? 'SCFH' : UNIT_STANDARD_L_HOUR;
};
export const getLiquidUnit = (regionAlias) => {
    const regionCategory = getRegionCategory(regionAlias);
    return regionCategory === 'America' ? 'US-GPM' : 'L/min';
};
export const getTemperatureUnit = (regionAlias) => {
    const regionCategory = getRegionCategory(regionAlias);
    return regionCategory === 'America' ? '°F' : '°C';
};
export const getLeakageRateUnit = (testMedia, regionAlias) => {
    if (testMedia === 'Gas') {
        return getGasUnit(regionAlias);
    }
    if (testMedia === 'Liquid') {
        return getLiquidUnit(regionAlias);
    }
    return '';
};
